import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { io, Socket } from 'socket.io-client';

import { poOdmowie401 } from '../lib/martwaSesja';
import { supabase } from '../lib/supabase';
import { computeBackoffDelay, secondsUntil } from '../lib/wsBackoff';
import type { WsConnectionState } from '../lib/wsConnectionStatus';

export interface NatsMessage {
  subject: string;
  data: unknown;
}

export type { WsConnectionState };

/**
 * Rdzeń kontekstu — to, czego potrzebuje KAŻDY konsument strumienia zdarzeń.
 * Zmienia się wyłącznie przy faktycznym połączeniu/rozłączeniu.
 *
 * DLACZEGO stan ponawiania mieszka osobno (`WsRetryStatusContext`): kontekst
 * przerysowuje każdego swojego konsumenta przy KAŻDEJ zmianie wartości, a
 * `retryInSeconds` tyka co sekundę i `connectionState`/`reconnectAttempt` zmieniają
 * się przy każdej próbie. Trzymane razem z rdzeniem przerysowywały m.in.
 * `LivingNetwork` (kanwa globu), który o ponawianiu nic nie wie. W CI nie ma bramki
 * WS, więc backoff nigdy nie ustaje — drzewo przerysowywało się bez końca i
 * Playwright nie mógł kliknąć „Wyloguj": przycisk był znajdowany, ale odpinany od
 * DOM, zanim klik zdążył wylądować („element was detached from the DOM, retrying").
 * `force: true` tego nie obchodzi — to nie jest przechwycony hit-test, tylko
 * znikający węzeł.
 */
interface WebSocketContextValue {
  isConnected: boolean;
  /** Natychmiastowa próba połączenia, z pominięciem odliczania. */
  reconnectNow: () => void;
  subscribeRaw: (callback: (message: NatsMessage) => void) => () => void;
}

const WebSocketContext = createContext<WebSocketContextValue | undefined>(undefined);

/** Stan ponawiania — zmienny, konsumowany wyłącznie przez panel aktywności. */
interface WsRetryStatusValue {
  connectionState: WsConnectionState;
  /** Liczba nieudanych prób z rzędu (0 po udanym połączeniu). */
  reconnectAttempt: number;
  /** Sekundy do następnej automatycznej próby; null gdy żadna nie jest zaplanowana. */
  retryInSeconds: number | null;
}

const WsRetryStatusContext = createContext<WsRetryStatusValue>({
  connectionState: 'connecting',
  reconnectAttempt: 0,
  retryInSeconds: null,
});

// Brak sesji Supabase nie jest błędem sieci — nie ma sensu rozpędzać backoffu,
// wystarczy spokojny watchdog na wypadek, gdyby zdarzenie auth nas ominęło.
const UNAUTHENTICATED_RETRY_MS = 30_000;

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<WsConnectionState>('connecting');
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [retryInSeconds, setRetryInSeconds] = useState<number | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const rawSubscriptionsRef = useRef<Set<(message: NatsMessage) => void>>(new Set());
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptRef = useRef(0);
  const connectGenerationRef = useRef(0);
  const isUnmountedRef = useRef(false);
  // Brak VITE_WS_URL / URL localhost na produkcji — trwałe wyłączenie z powodu
  // konfiguracji, jedyny przypadek, w którym nie ponawiamy.
  const isUnconfiguredRef = useRef(false);
  // Bufor zdarzeń, które przyszły zanim zarejestrował się pierwszy subskrybent.
  const eventBufferRef = useRef<NatsMessage[]>([]);
  const maxBufferSize = 50;

  const clearTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // `connect` i `scheduleReconnect` wołają się nawzajem — ref przerywa cykl
  // zależności bez odtwarzania callbacków przy każdym renderze.
  const connectRef = useRef<() => void>(() => {});

  const scheduleReconnect = useCallback(
    (delayMs: number) => {
      if (isUnmountedRef.current || isUnconfiguredRef.current) return;
      clearTimers();

      const dueAt = Date.now() + delayMs;
      setRetryInSeconds(secondsUntil(dueAt, Date.now()));

      countdownIntervalRef.current = setInterval(() => {
        setRetryInSeconds(secondsUntil(dueAt, Date.now()));
      }, 1000);

      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        setRetryInSeconds(null);
        connectRef.current();
      }, delayMs);
    },
    [clearTimers]
  );

  /** Rejestruje niepowodzenie i planuje kolejną próbę z rosnącym opóźnieniem. */
  const failAndRetry = useCallback(() => {
    if (isUnmountedRef.current || isUnconfiguredRef.current) return;
    attemptRef.current += 1;
    setReconnectAttempt(attemptRef.current);
    setConnectionState('reconnecting');
    scheduleReconnect(computeBackoffDelay(attemptRef.current));
  }, [scheduleReconnect]);

  const connect = useCallback(async () => {
    if (isUnmountedRef.current || typeof window === 'undefined') return;
    if (isUnconfiguredRef.current) return;

    // Znacznik tej próby. Między `connect()` a utworzeniem socketu jest await na
    // sesji Supabase — bez tego dwie próby (np. podwójny montaż w StrictMode lub
    // „Połącz teraz" w trakcie odliczania) mogłyby otworzyć dwa sockety, z czego
    // jeden zostałby osierocony i dublował zdarzenia.
    const generation = ++connectGenerationRef.current;

    const wsUrl = import.meta.env.VITE_WS_URL;

    if (!wsUrl) {
      isUnconfiguredRef.current = true;
      setConnectionState('unconfigured');
      setRetryInSeconds(null);
      return;
    }

    // URL na localhost w buildzie produkcyjnym to błąd konfiguracji deploymentu,
    // a nie chwilowa awaria — ponawianie tylko zaśmiecałoby konsolę.
    if (wsUrl.includes('localhost') && import.meta.env.PROD) {
      isUnconfiguredRef.current = true;
      setConnectionState('unconfigured');
      setRetryInSeconds(null);
      return;
    }

    // Poprzedni socket musi zniknąć, zanim otworzymy nowy — inaczej po kilku
    // ponowieniach mielibyśmy równoległe połączenia i zdublowane zdarzenia.
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setConnectionState((prev) => (prev === 'connected' ? prev : 'connecting'));

    let session: { access_token: string } | null = null;
    try {
      const { data } = await supabase.auth.getSession();
      session = data.session ? { access_token: data.session.access_token } : null;
    } catch (error) {
      // AbortError z navigator.locks jest nieszkodliwy (StrictMode / zmiana karty).
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error('WebSocket: nie udało się odczytać sesji', error);
      }
      failAndRetry();
      return;
    }

    if (isUnmountedRef.current || generation !== connectGenerationRef.current) return;

    if (!session?.access_token) {
      // Czekamy na logowanie; onAuthStateChange podniesie połączenie od razu,
      // watchdog jest tylko zabezpieczeniem na wypadek zgubionego zdarzenia.
      setConnectionState('unauthenticated');
      setIsConnected(false);
      scheduleReconnect(UNAUTHENTICATED_RETRY_MS);
      return;
    }

    const socket = io(wsUrl, {
      auth: { token: session.access_token },
      transports: ['websocket', 'polling'],
      // Ponawianiem steruje ten provider (wykładniczy backoff bez limitu prób);
      // wbudowany mechanizm socket.io poddawał się po `reconnectionAttempts`.
      reconnection: false,
      timeout: 20_000,
    });

    socket.on('connect', () => {
      clearTimers();
      attemptRef.current = 0;
      setReconnectAttempt(0);
      setRetryInSeconds(null);
      setIsConnected(true);
      setConnectionState('connected');
      // Bez emitów `subscribe` z klienta: brama sama subskrybuje socket do
      // swojego zestawu kanałów po uwierzytelnieniu.
    });

    socket.on('disconnect', (reason) => {
      setIsConnected(false);
      // Także dla 'io server disconnect' — restart bramy nie może kończyć się
      // martwym feedem do końca życia karty.
      console.log('WebSocket rozłączony:', reason);
      failAndRetry();
    });

    socket.on('connect_error', () => {
      setIsConnected(false);
      failAndRetry();
    });

    socket.on('error', (error: { message?: string } | undefined) => {
      console.error('WebSocket error:', error);
      // Brama odrzuca token po `auth.getUser` (`Invalid authentication`), a
      // potem rozłącza — bez tego pytania backoff ponawiałby martwą sesję co
      // 30 s do końca życia karty, a karta NEXUS pokazywałaby „Offline"
      // zamiast ekranu logowania (lib/martwaSesja.ts).
      if (error?.message === 'Invalid authentication') {
        void poOdmowie401('websocket-gateway');
      }
    });

    socket.on('nats-event', (message: NatsMessage) => {
      if (rawSubscriptionsRef.current.size === 0) {
        eventBufferRef.current.push(message);
        if (eventBufferRef.current.length > maxBufferSize) {
          eventBufferRef.current.shift();
        }
      } else {
        rawSubscriptionsRef.current.forEach((cb) => cb(message));
      }
    });

    socketRef.current = socket;
  }, [clearTimers, failAndRetry, scheduleReconnect]);

  connectRef.current = () => {
    void connect();
  };

  const reconnectNow = useCallback(() => {
    if (isUnconfiguredRef.current) return;
    clearTimers();
    setRetryInSeconds(null);
    attemptRef.current = 0;
    setReconnectAttempt(0);
    void connect();
  }, [clearTimers, connect]);

  useEffect(() => {
    isUnmountedRef.current = false;
    void connect();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        clearTimers();
        setRetryInSeconds(null);
        if (socketRef.current) {
          socketRef.current.removeAllListeners();
          socketRef.current.disconnect();
          socketRef.current = null;
        }
        setIsConnected(false);
        setConnectionState('unauthenticated');
        return;
      }

      // INITIAL_SESSION / SIGNED_IN / TOKEN_REFRESHED — token się zmienił,
      // więc otwieramy połączenie od nowa z aktualnym tokenem.
      if (session && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
        clearTimers();
        setRetryInSeconds(null);
        attemptRef.current = 0;
        setReconnectAttempt(0);
        void connect();
      }
    });

    // Powrót sieci lub karty na pierwszy plan to najlepszy moment na próbę —
    // użytkownik nie musi czekać do końca odliczania.
    const retryIfDown = () => {
      if (!socketRef.current?.connected && !isUnconfiguredRef.current) {
        reconnectNow();
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') retryIfDown();
    };
    window.addEventListener('online', retryIfDown);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      isUnmountedRef.current = true;
      subscription.unsubscribe();
      window.removeEventListener('online', retryIfDown);
      document.removeEventListener('visibilitychange', onVisible);
      clearTimers();
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
    // `connect` i `reconnectNow` są stabilne (useCallback bez zmiennych zależności).
  }, [connect, reconnectNow, clearTimers]);

  const subscribeRaw = useCallback((callback: (message: NatsMessage) => void) => {
    rawSubscriptionsRef.current.add(callback);

    if (eventBufferRef.current.length > 0) {
      eventBufferRef.current.forEach((msg) => callback(msg));
      eventBufferRef.current = [];
    }

    return () => {
      rawSubscriptionsRef.current.delete(callback);
    };
  }, []);

  // Memoizacja jest tu konieczna, nie kosmetyczna: bez niej każdy render providera
  // (a tyka w nim odliczanie) tworzyłby nowy obiekt i przerysowywał konsumentów
  // mimo niezmienionego stanu połączenia.
  const core = useMemo(
    () => ({ isConnected, reconnectNow, subscribeRaw }),
    [isConnected, reconnectNow, subscribeRaw]
  );

  const retryStatus = useMemo(
    () => ({ connectionState, reconnectAttempt, retryInSeconds }),
    [connectionState, reconnectAttempt, retryInSeconds]
  );

  return (
    <WebSocketContext.Provider value={core}>
      <WsRetryStatusContext.Provider value={retryStatus}>{children}</WsRetryStatusContext.Provider>
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}

/**
 * Stan ponawiania połączenia. Osobny hook, żeby zmiany co sekundę przerysowywały
 * wyłącznie to, co ten stan POKAZUJE (panel aktywności), a nie każdego konsumenta
 * `useWebSocket` — patrz komentarz przy `WebSocketContextValue`.
 */
export function useWsRetryStatus() {
  return useContext(WsRetryStatusContext);
}
